const PROOFREADING_CODES = ['sp', 'p', 't', 'g', 'reg', 'wo'];
const fold = text => String(text || '').normalize('NFKC').replace(/[‘’]/g, "'").replace(/\s+/g, ' ').trim();

function validProofreadingBox(box) {
  if (!Array.isArray(box) || box.length !== 4 || !box.every(value => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1000)) return null;
  const [top, left, bottom, right] = box;
  if (bottom <= top || right <= left || bottom - top > 80 || right - left > 850) return null;
  return { x: left / 1000, y: top / 1000, width: (right - left) / 1000, height: (bottom - top) / 1000 };
}

function collectInlineAnnotations(results) {
  const annotations = new Map();
  for (const result of results || []) {
    for (const raw of (Array.isArray(result?.inline_annotations) ? result.inline_annotations : []).slice(0, 80)) {
      if (!raw || typeof raw !== 'object' || typeof raw.correction !== 'string') continue;
      if (!PROOFREADING_CODES.includes(raw.error_code) || !fold(raw.exact_quote) || !fold(raw.context_snippet) || !raw.correction) continue;
      const answer = fold(result.learnerAnswer);
      if (!answer.includes(fold(raw.exact_quote)) || !fold(raw.context_snippet).includes(fold(raw.exact_quote)) || !answer.includes(fold(raw.context_snippet))) continue;
      const page = Number(raw.page);
      const box = raw.sourceCoordinate === 'vision_proofreading_anchor' ? validProofreadingBox(raw.bbox_2d) : null;
      const placed = box && Number.isInteger(page) && page > 0;
      const key = `${result.questionNumber}|${raw.exact_quote}|${raw.context_snippet}|${raw.error_code}`;
      annotations.set(key, { ...raw, id: key, questionNumber: result.questionNumber, page: placed ? page : null,
        answerBox: result.answerBox || null, pageHint: Math.max(1, Number(result.evidencePage) || 1), rects: placed ? [box] : [], needsPlacement: !placed });
    }
  }
  return [...annotations.values()];
}

function occurrences(text, needle) {
  const out = []; let start = 0;
  while (needle && (start = text.indexOf(needle, start)) !== -1) {
    const word = /[\p{L}\p{N}_]/u;
    if (!(word.test(needle[0]) && start > 0 && word.test(text[start - 1]))
      && !(word.test(needle[needle.length - 1]) && word.test(text[start + needle.length] || ' '))) out.push(start);
    start += Math.max(1, needle.length);
  }
  return out;
}

function textCandidates(annotation, page) {
  let text = ''; const spans = [];
  for (const word of page.words) {
    const token = fold(word.text);
    if (!token) continue;
    if (text) text += ' ';
    const start = text.length; text += token;
    spans.push({ start, end: text.length, word });
  }
  const quote = fold(annotation.exact_quote);
  const context = fold(annotation.context_snippet);
  const contexts = occurrences(text, context);
  return occurrences(text, quote).filter(start => contexts.some(at => start >= at && start + quote.length <= at + context.length)).map(start => {
    const words = spans.filter(span => span.end > start && span.start < start + quote.length).map(span => span.word);
    const lines = [];
    for (const word of words) {
      const center = word.y + word.height / 2;
      let line = lines.find(candidate => Math.abs(candidate.center - center) < Math.min(.008, word.height * .5));
      if (!line) { line = { center, words: [] }; lines.push(line); }
      line.words.push(word);
    }
    return { page: page.page, rects: lines.map(line => {
      const x = Math.min(...line.words.map(word => word.x));
      const y = Math.min(...line.words.map(word => word.y));
      return { x, y, width: Math.max(...line.words.map(word => word.x + word.width)) - x,
        height: Math.max(...line.words.map(word => word.y + word.height)) - y };
    }) };
  });
}

function locateInlineInText(annotations, pages) {
  return annotations.map(annotation => {
    let candidates = pages.flatMap(page => textCandidates(annotation, page));
    const box = !annotation.needsPlacement ? annotation.rects[0] : annotation.answerBox;
    const page = !annotation.needsPlacement ? annotation.page : annotation.pageHint;
    if (box) {
      candidates = candidates.filter(candidate => candidate.page === page && candidate.rects.some(rect =>
        rect.x < box.x + box.width + .015 && rect.x + rect.width > box.x - .015
        && rect.y < box.y + box.height + .015 && rect.y + rect.height > box.y - .015));
    }
    if (candidates.length !== 1) return annotation;
    return { ...annotation, ...candidates[0], needsPlacement: false, sourceCoordinate: 'pdf_text_proofreading_anchor' };
  });
}

function buildInlineBadges(annotations) {
  const groups = [];
  for (const annotation of annotations.filter(item => !item.needsPlacement && item.rects?.length)) {
    const rect = annotation.rects[0]; const y = rect.y + rect.height;
    let group = groups.find(item => item.page === annotation.page && Math.abs(item.targetY - y) < .009);
    if (!group) { group = { page: annotation.page, targetY: y, y, codes: [], annotations: [] }; groups.push(group); }
    if (!group.codes.includes(annotation.error_code)) group.codes.push(annotation.error_code);
    group.annotations.push(annotation);
  }
  for (const page of new Set(groups.map(group => group.page))) {
    const rows = groups.filter(group => group.page === page).sort((a, b) => a.targetY - b.targetY);
    const gap = Math.min(.018, .94 / Math.max(1, rows.length));
    rows.forEach((row, index) => { row.y = Math.max(.02, row.targetY, index ? rows[index - 1].y + gap : 0); });
    for (let index = rows.length - 1; index >= 0; index -= 1) rows[index].y = Math.min(rows[index].y, index === rows.length - 1 ? .98 : rows[index + 1].y - gap);
  }
  return groups;
}

function viewportToPdfPoint(point, box, rotation = 0) {
  switch (((rotation % 360) + 360) % 360) {
    case 90: return { x: box.x + point.y * box.width, y: box.y + point.x * box.height };
    case 180: return { x: box.x + (1 - point.x) * box.width, y: box.y + point.y * box.height };
    case 270: return { x: box.x + (1 - point.y) * box.width, y: box.y + (1 - point.x) * box.height };
    default: return { x: box.x + point.x * box.width, y: box.y + (1 - point.y) * box.height };
  }
}

export { PROOFREADING_CODES, validProofreadingBox, collectInlineAnnotations, locateInlineInText, buildInlineBadges, viewportToPdfPoint };
